import { useState } from "react";
import { APP_VERSION, APP_UPDATED } from "../../constants/version";
import { DEMO_MODE } from "../../utils/firebase";

/* ─── Washi colours cycle across profile cards ─── */
const WASHI_COLORS = [
  { tape: "bg-pink-300",   border: "border-pink-300",   badge: "bg-pink-100 text-pink-700"   },
  { tape: "bg-blue-300",   border: "border-blue-300",   badge: "bg-blue-100 text-blue-700"   },
  { tape: "bg-green-300",  border: "border-green-300",  badge: "bg-green-100 text-green-700" },
  { tape: "bg-amber-300",  border: "border-amber-300",  badge: "bg-amber-100 text-amber-700" },
  { tape: "bg-purple-300", border: "border-purple-300", badge: "bg-purple-100 text-purple-700" },
];

/* Card tilt rotations cycle per index */
const TILTS = ["-rotate-1", "rotate-1", "-rotate-0.5", "rotate-0.5", "-rotate-1.5"];

/* ─── PIN dot row ─── */
function PinDots({ length }) {
  return (
    <div className="flex justify-center gap-3 mb-5">
      {[0, 1, 2, 3].map(i => (
        <div
          key={i}
          className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg border-2 flex items-center justify-center transition-all duration-150
            ${i < length
              ? "bg-amber-500 border-amber-500 scale-105"
              : "bg-stone-50 border-stone-300"
            }`}
        >
          {i < length && <span className="text-white text-lg leading-none">●</span>}
        </div>
      ))}
    </div>
  );
}

/* ─── Number pad ─── */
function NumPad({ onKey }) {
  const keys = ["1","2","3","4","5","6","7","8","9","","0","⌫"];
  return (
    <div className="grid grid-cols-3 gap-2">
      {keys.map((k, i) => {
        if (!k) return <div key={i} />;
        if (k === "⌫") {
          return (
            <button
              key={i}
              onClick={() => onKey("⌫")}
              className="h-12 sm:h-14 rounded-lg bg-red-50 border border-red-200 text-red-500 flex items-center justify-center transition-all duration-150 hover:bg-red-100 active:scale-95"
              aria-label="Delete"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l6.414 6.414a2 2 0 001.414.586H19a2 2 0 002-2V7a2 2 0 00-2-2h-8.172a2 2 0 00-1.414.586L3 12z"/>
              </svg>
            </button>
          );
        }
        return (
          <button
            key={i}
            onClick={() => onKey(k)}
            className="h-12 sm:h-14 rounded-lg bg-amber-50 border border-amber-200 font-handwritten font-bold text-xl text-amber-800 transition-all duration-150 hover:bg-amber-100 active:scale-95 active:bg-amber-200"
          >
            {k}
          </button>
        );
      })}
    </div>
  );
}

/* ─── Main Login Screen ─── */
export function PlannerLoginScreen({ users, onLogin, appName }) {
  const [sel, setSel]     = useState(null);
  const [pin, setPin]     = useState("");
  const [err, setErr]     = useState("");
  const [shake, setShake] = useState(false);

  const user = users.find(u => u.id === sel);

  function pick(u) {
    setSel(u.id);
    setPin("");
    setErr("");
    setShake(false);
  }

  function pinKey(k) {
    if (k === "⌫") { setPin(p => p.slice(0, -1)); setErr(""); return; }
    const next = pin + k;
    if (next.length > 4) return;
    setPin(next);
    setErr("");
    if (next.length === 4) {
      if (next === user.pin) {
        onLogin(user);
      } else {
        setShake(true);
        setErr("Oops! Wrong PIN. Try again!");
        setTimeout(() => { setPin(""); setErr(""); setShake(false); }, 900);
      }
    }
  }

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4 sm:p-6 relative overflow-hidden"
      style={{
        backgroundColor: "#c4956a",
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%23a67c52' fill-opacity='0.3'%3E%3Cpath opacity='.5' d='M96 95h4v1h-4v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9zm-1 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm9-10v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm9-10v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm9-10v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm9-10v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9z'/%3E%3Cpath d='M6 5V0H5v5H0v1h5v94h1V6h94V5H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      }}
    >
      {/* Decorative pencil emojis — hidden on mobile */}
      <div className="absolute top-8 left-8 text-3xl opacity-60 hidden sm:block" style={{ transform: "rotate(-25deg)" }}>✏️</div>
      <div className="absolute bottom-12 right-12 text-3xl opacity-60 hidden sm:block" style={{ transform: "rotate(15deg)" }}>📝</div>

      <div className="relative w-full max-w-2xl">

        {/* ── Header card ── */}
        <div className="paper-texture rounded-xl p-6 sm:p-8 mb-6 relative index-card">
          {/* Push pins */}
          <div className="absolute -top-2.5 left-8 w-5 h-5 rounded-full bg-red-500 shadow-md" style={{ boxShadow: "0 2px 4px rgba(0,0,0,0.3), inset 0 -2px 4px rgba(0,0,0,0.2)" }} />
          <div className="absolute -top-2.5 right-8 w-5 h-5 rounded-full bg-blue-500 shadow-md" style={{ boxShadow: "0 2px 4px rgba(0,0,0,0.3), inset 0 -2px 4px rgba(0,0,0,0.2)" }} />
          {/* Washi tape across top */}
          <div
            className="absolute -top-3 left-1/2 -translate-x-1/2 w-20 h-6 rounded opacity-85"
            style={{
              background: "repeating-linear-gradient(45deg, #fbbf24, #fbbf24 4px, #f59e0b 4px, #f59e0b 8px)",
              transform: "translateX(-50%) rotate(1deg)",
            }}
          />
          <div className="text-center pt-4">
            <div className="text-5xl sm:text-6xl mb-2">📚</div>
            <h1 className="font-handwritten text-4xl sm:text-5xl text-amber-800 mb-1">{appName || "SpellWise Kids"}</h1>
            <p className="text-stone-500 text-sm sm:text-base">Family Learning Planner</p>
          </div>
        </div>

        {/* ── Who's learning today label ── */}
        <div className="text-center mb-4">
          <span className="inline-block bg-amber-100 border border-amber-300 rounded-full px-4 py-1 text-amber-800 font-handwritten text-xl">
            Who's learning today?
          </span>
        </div>

        {/* ── Profile cards grid ── */}
        <div className={`grid gap-3 sm:gap-4 mb-6 px-2 ${
          users.length <= 2 ? "grid-cols-2" :
          users.length === 3 ? "grid-cols-3" :
          "grid-cols-2 sm:grid-cols-4"
        }`}>
          {users.map((u, i) => {
            const w = WASHI_COLORS[i % WASHI_COLORS.length];
            const tilt = TILTS[i % TILTS.length];
            const isSelected = sel === u.id;
            return (
              <div
                key={u.id}
                onClick={() => pick(u)}
                className={`paper-texture rounded-xl p-4 cursor-pointer relative border-2 hover-lift transition-all duration-200 ${tilt}
                  ${isSelected
                    ? `${w.border} shadow-lg`
                    : "border-transparent"
                  }`}
                style={isSelected ? { boxShadow: "0 0 0 3px rgba(217,119,6,0.25), 0 4px 12px rgba(0,0,0,0.1)" } : {}}
              >
                {/* Washi tape top */}
                <div
                  className={`absolute -top-2 left-1/2 w-14 h-4 rounded opacity-85 ${w.tape}`}
                  style={{ transform: `translateX(-50%) rotate(${i % 2 === 0 ? "2deg" : "-1deg"})` }}
                />
                <div className="text-center pt-2">
                  {/* Avatar */}
                  <div className="w-14 h-14 sm:w-18 sm:h-18 mx-auto bg-stone-100 rounded-full flex items-center justify-center text-3xl sm:text-4xl mb-2 border-2 border-stone-200">
                    {u.avatar}
                  </div>
                  {/* Name */}
                  <p className="font-handwritten text-xl sm:text-2xl text-stone-800">{u.name}</p>
                  {/* Role badge */}
                  <span className={`inline-block text-xs px-2 py-0.5 rounded-full mt-1 ${w.badge}`}>
                    {u.role === "parent" ? "Admin" : `Year ${u.yearLevel}`}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── PIN entry card — only shown when a profile is selected ── */}
        {user && (
          <div
            className={`paper-texture rounded-xl p-5 sm:p-6 relative index-card max-w-xs mx-auto transition-all duration-200 ${shake ? "animate-shake" : ""}`}
          >
            {/* Push pin */}
            <div
              className="absolute -top-2.5 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-yellow-400 shadow-md"
              style={{ boxShadow: "0 2px 4px rgba(0,0,0,0.3), inset 0 -2px 4px rgba(0,0,0,0.2)" }}
            />

            <div className="text-center mb-4">
              <p className="font-handwritten text-xl text-stone-600">Enter your secret PIN</p>
              <p className="text-xs text-stone-400 mt-1">
                for {user.name}
                {user.role === "child" && user.mustResetPin ? " (default: 1234)" : ""}
              </p>
            </div>

            <PinDots length={pin.length} />
            <NumPad onKey={pinKey} />

            {err && (
              <p className="mt-3 text-center text-red-500 text-sm font-medium animate-pulse">{err}</p>
            )}
          </div>
        )}
      </div>

      {/* ── Version footer ── */}
      <div className="mt-6 text-center">
        <p className="text-amber-900 text-xs font-medium opacity-50">
          v{APP_VERSION} · Updated {APP_UPDATED} · {DEMO_MODE ? "Local Mode" : "Firebase Connected"}
        </p>
      </div>

      {/* Shake animation */}
      <style>{`
        @keyframes planner-shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-8px); }
          40%, 80% { transform: translateX(8px); }
        }
        .animate-shake { animation: planner-shake 0.4s ease-in-out; }
      `}</style>
    </div>
  );
}
